import * as Polynomial from 'polynomial'
import TransferFunction from './TransferFunction'

export enum FilterType {
  LOWPASS_1P,
  HIGHPASS_1P,
  LOWPASS,
  HIGHPASS,
  BANDPASS,
  NOTCH,
  PEAK,
  LOWSHELF,
  HIGHSHELF,
}

/**
 * Given a few basic filter parameters, this class will output a
 * TransferFunction.
 */
export default class BiquadCalculator {
  // Coefficient calculation from
  // http://www.earlevel.com/scripts/widgets/20131013/biquads2.js, but where did
  // they come from?
  //
  // Also looking at https://web.stanford.edu/~boyd/ee102/conv_demo.pdf which
  // seems to be more useful.
  // TODO: Look at Real Audio Synthesis book by Perry (rec by Chris Rogers).
  static calculateTransferFunction(filterType: FilterType, Fc: number, Fs: number,
      Q: number, peakGain: number) {
    let a0, a1, a2, b1, b2;
    let norm;
    let V = Math.pow(10, Math.abs(peakGain) / 20);
    let K = Math.tan(Math.PI * Fc / Fs);

    switch (filterType) {
      case FilterType.LOWPASS_1P:
        b1 = Math.exp(-2.0 * Math.PI * (Fc / Fs))
        a0 = 1.0 - b1
        b1 = -b1
        a1 = a2 = b2 = 0
        break

      case FilterType.HIGHPASS_1P:
        b1 = -Math.exp(-2.0 * Math.PI * (0.5 - Fc / Fs))
        a0 = 1.0 + b1
        b1 = -b1
        a1 = a2 = b2 = 0
        break

      case FilterType.LOWPASS:
        norm = 1 / (1 + K / Q + K * K);
        a0 = K * K * norm;
        a1 = 2 * a0;
        a2 = a0;
        b1 = 2 * (K * K - 1) * norm;
        b2 = (1 - K / Q + K * K) * norm;
        break;

      case FilterType.HIGHPASS:
        norm = 1 / (1 + K / Q + K * K);
        a0 = 1 * norm;
        a1 = -2 * a0;
        a2 = a0;
        b1 = 2 * (K * K - 1) * norm;
        b2 = (1 - K / Q + K * K) * norm;
        break;

      case FilterType.BANDPASS:
        norm = 1 / (1 + K / Q + K * K);
        a0 = K / Q * norm;
        a1 = 0;
        a2 = -a0;
        b1 = 2 * (K * K - 1) * norm;
        b2 = (1 - K / Q + K * K) * norm;
        break;

      case FilterType.NOTCH:
        norm = 1 / (1 + K / Q + K * K);
        a0 = (1 + K * K) * norm;
        a1 = 2 * (K * K - 1) * norm;
        a2 = a0;
        b1 = a1;
        b2 = (1 - K / Q + K * K) * norm;
        break;

      case FilterType.PEAK:
        if (peakGain >= 0) {
          norm = 1 / (1 + 1/Q * K + K * K);
          a0 = (1 + V/Q * K + K * K) * norm;
          a1 = 2 * (K * K - 1) * norm;
          a2 = (1 - V/Q * K + K * K) * norm;
          b1 = a1;
          b2 = (1 - 1/Q * K + K * K) * norm;
        } else {
          norm = 1 / (1 + V/Q * K + K * K);
          a0 = (1 + 1/Q * K + K * K) * norm;
          a1 = 2 * (K * K - 1) * norm;
          a2 = (1 - 1/Q * K + K * K) * norm;
          b1 = a1;
          b2 = (1 - V/Q * K + K * K) * norm;
        }
        break;

      case FilterType.LOWSHELF:
        if (peakGain >= 0) {
          norm = 1 / (1 + Math.SQRT2 * K + K * K);
          a0 = (1 + Math.sqrt(2*V) * K + V * K * K) * norm;
          a1 = 2 * (V * K * K - 1) * norm;
          a2 = (1 - Math.sqrt(2*V) * K + V * K * K) * norm;
          b1 = 2 * (K * K - 1) * norm;
          b2 = (1 - Math.SQRT2 * K + K * K) * norm;
        } else {
          norm = 1 / (1 + Math.sqrt(2*V) * K + V * K * K);
          a0 = (1 + Math.SQRT2 * K + K * K) * norm;
          a1 = 2 * (K * K - 1) * norm;
          a2 = (1 - Math.SQRT2 * K + K * K) * norm;
          b1 = 2 * (V * K * K - 1) * norm;
          b2 = (1 - Math.sqrt(2*V) * K + V * K * K) * norm;
        }
        break;

      case FilterType.HIGHSHELF:
        if (peakGain >= 0) {
          norm = 1 / (1 + Math.SQRT2 * K + K * K);
          a0 = (V + Math.sqrt(2*V) * K + K * K) * norm;
          a1 = 2 * (K * K - V) * norm;
          a2 = (V - Math.sqrt(2*V) * K + K * K) * norm;
          b1 = 2 * (K * K - 1) * norm;
          b2 = (1 - Math.SQRT2 * K + K * K) * norm;
        } else {
          norm = 1 / (V + Math.sqrt(2*V) * K + K * K);
          a0 = (1 + Math.SQRT2 * K + K * K) * norm;
          a1 = 2 * (K * K - 1) * norm;
          a2 = (1 - Math.SQRT2 * K + K * K) * norm;
          b1 = 2 * (K * K - V) * norm;
          b2 = (V - Math.sqrt(2*V) * K + K * K) * norm;
        }
        break;
    }
    const numerator = new Polynomial([a2, a1, a0])
    const denominator = new Polynomial([b2, b1, 1])
    return new TransferFunction(numerator, denominator)
  }
}



// WEBPACK FOOTER //
// ./src/BiquadCalculator.ts